import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { RatingSlider } from "../components/RatingSlider";
import { formatRating } from "../utils/ratings";
import PhotoAvatar from "../components/photos/PhotoAvatar";
import RatingMessageField from "../components/RatingMessageField";
import { 
  Target, Users, Calendar, ArrowLeft, User, 
  ChevronLeft, Plus, Dumbbell, Activity,
  CheckCircle2, Trash2, Zap, Brain, Eye, Pencil,
  Smile, BookOpen, Gauge, Repeat, ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import { 
  ResponsiveContainer, RadarChart, 
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { useUser } from '../context/UserContext';
import ActivityReminderService from '../utils/ActivityReminderService';
import repairAthleteSessionTimestamps from '../utils/sessionTimestampRepair';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme Hook
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('mygoal-theme');
    return saved ? saved === 'dark' : true;
  });
  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('mygoal-theme', newTheme ? 'dark' : 'light');
  };
  return { isDark, toggleTheme };
};

// Theme classes helper
const useThemeClasses = (isDark) => ({
  bg: isDark ? 'bg-[#0a0a0f]' : 'bg-gradient-to-br from-[#f8f7ff] via-[#f0eeff] to-[#e8e4ff]',
  card: isDark ? 'bg-[#1a1a2e]/80 border-white/10' : 'bg-white/80 border-[#493986]/10 shadow-lg',
  textPrimary: isDark ? 'text-white' : 'text-[#493986]',
  textSecondary: isDark ? 'text-white/60' : 'text-[#493986]/60',
  textMuted: isDark ? 'text-white/50' : 'text-[#493986]/50',
  input: isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'bg-white border-[#493986]/20 text-[#493986] placeholder-[#493986]/30',
  button: isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-[#493986]/5 hover:bg-[#493986]/10',
});

// Swipeable Modal Hook - only swipe when at scroll top
const useSwipeToClose = (onClose) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [canSwipe, setCanSwipe] = useState(false);

  const minSwipeDistance = 100;

  const onTouchStart = (e) => {
    if (e.target.closest('input[type="range"]')) {
      setTouchStart(null); setTouchEnd(null); setCanSwipe(false); setIsDragging(false);
      return;
    }
    // Check if we're at the top of the scrollable content
    const scrollElement = e.currentTarget;
    const isAtTop = scrollElement.scrollTop <= 0;
    
    setCanSwipe(isAtTop);
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
    setIsDragging(true);
  };

  const onTouchMove = (e) => {
    if (!touchStart) return;
    
    const currentTouch = e.targetTouches[0].clientY;
    const diff = currentTouch - touchStart;
    
    // Only allow swipe down gesture if we started at the top
    if (diff > 0 && canSwipe) {
      setTranslateY(diff);
      setTouchEnd(currentTouch);
    } else if (diff < 0) {
      // User is scrolling up within content, reset swipe state
      setCanSwipe(false);
      setTranslateY(0);
    }
  };

  const onTouchEnd = () => {
    setIsDragging(false);
    
    if (!touchStart || !touchEnd || !canSwipe) {
      setTranslateY(0);
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    const distance = touchEnd - touchStart;
    const isSwipeDown = distance > minSwipeDistance;
    
    if (isSwipeDown) {
      setTranslateY(window.innerHeight);
      setTimeout(() => {
        onClose();
        setTranslateY(0);
      }, 200);
    } else {
      setTranslateY(0);
    }
    
    setTouchStart(null);
    setTouchEnd(null);
    setCanSwipe(false);
  };

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    translateY,
    isDragging
  };
};

// Category Icons for Goals
const categoryIcons = { technical: Eye, physical: Zap, tactical: Brain, mental: Target };

export const AgendaPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const handledReminder = useRef('');
  const [activityDataLoaded, setActivityDataLoaded] = useState(false);
  const [personalDataLoaded, setPersonalDataLoaded] = useState(false);
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, isCoach } = useUser();
  const isAthlete = user?.account_type === 'athlete';
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [showGoalSelection, setShowGoalSelection] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showActivityDetail, setShowActivityDetail] = useState(false);
  const messageRef = useRef(null);
  const [ratings, setRatings] = useState({ fun: 5, learning: 5, mygoal: 5, intensity: 5 });
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [customActivities, setCustomActivities] = useState(() => {
    const saved = localStorage.getItem('mygoal-custom-activities');
    return saved ? JSON.parse(saved) : [];
  });
  const [activityGoals, setActivityGoals] = useState(() => {
    const saved = localStorage.getItem('mygoal-activity-goals');
    return saved ? JSON.parse(saved) : {};
  });
  const [selectedGoalsForActivity, setSelectedGoalsForActivity] = useState([]);
  const [goalRatings, setGoalRatings] = useState({});
  
  // Coach-specific state
  const [teamAthletes, setTeamAthletes] = useState([]);
  const [teamGoals, setTeamGoals] = useState([]);
  const [activityGoalsFromDb, setActivityGoalsFromDb] = useState([]);
  const [teamActivities, setTeamActivities] = useState([]); // Activities from coach/groups
  const [coachGroups, setCoachGroups] = useState([]); // Groups for coach to assign
  const [attendance, setAttendance] = useState({});
  const [attendanceSummary, setAttendanceSummary] = useState({});
  const [attendanceDetails, setAttendanceDetails] = useState({});
  const [activityAttendanceChoice, setActivityAttendanceChoice] = useState(true);
  const [showAllAthleteActivities, setShowAllAthleteActivities] = useState(false);
  
  // New/edit activity form state
  const getDefaultActivityForm = () => ({
    title: '',
    category: 'team_training',
    date: new Date().toISOString().split('T')[0],
    time: '18:00',
    duration: 90,
    location: '',
    selectedGroups: [],
    // Recurring options
    isRecurring: false,
    recurringFrequency: 'weekly', // 'daily', 'weekly'
    recurringEndDate: ''
  });
  const [newActivity, setNewActivity] = useState(getDefaultActivityForm());
  const [editingActivity, setEditingActivity] = useState(null);
  // 'single' = edit just this occurrence, 'series' = edit all in series
  const [editScope, setEditScope] = useState('single');
  const [showEditScopeModal, setShowEditScopeModal] = useState(false);
  const [pendingEditActivity, setPendingEditActivity] = useState(null);

  // Lock body scroll when any modal is open
  useEffect(() => {
    const isModalOpen = showAddActivity || showGoalSelection || showRating || showActivityDetail || showEditScopeModal;
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [showAddActivity, showGoalSelection, showRating, showActivityDetail, showEditScopeModal]);

  // Swipe handlers for modals
  const addActivitySwipe = useSwipeToClose(() => {
    setShowAddActivity(false);
    setEditingActivity(null);
    setNewActivity(getDefaultActivityForm());
  });
  const goalSelectionSwipe = useSwipeToClose(() => { setShowGoalSelection(false); setSelectedActivity(null); });
  const ratingSwipe = useSwipeToClose(() => { setShowRating(false); setSelectedActivity(null); });
  const activityDetailSwipe = useSwipeToClose(() => { setShowActivityDetail(false); setSelectedActivity(null); });

  // Fetch team data for coaches
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!user?.id || !isCoach) return;
      try {
        const teamRes = await axios.get(`${API}/coach/team?user_id=${user.id}`);
        setTeamAthletes(teamRes.data);
        
        const goalsPromises = teamRes.data.map(async (athlete) => {
          try {
            const goalsRes = await axios.get(`${API}/athlete/${athlete.id}/goals`);
            return {
              athlete_id: athlete.id,
              athlete_name: athlete.name,
              goals: goalsRes.data
            };
          } catch {
            return { athlete_id: athlete.id, athlete_name: athlete.name, goals: [] };
          }
        });
        const allTeamGoals = await Promise.all(goalsPromises);
        setTeamGoals(allTeamGoals);
      } catch (e) {
        console.error("Error fetching team data:", e);
      }
    };
    fetchTeamData();
  }, [user?.id, isCoach]);

  // Fetch team activities for athletes (from groups they belong to)
  useEffect(() => {
    const fetchTeamActivities = async () => {
      if (!user?.id || isCoach) return; // Only for athletes
      try {
        const res = await axios.get(`${API}/athlete/activities?athlete_id=${user.id}`);
        setTeamActivities(res.data);
        const summaryMap = {};
        res.data.forEach((activity) => {
          summaryMap[`team_${activity.id}`] = {
            attending_count: activity.attendance_count || 0,
            total_athletes: activity.total_athletes || 0,
          };
        });
        setAttendanceSummary((prev) => ({ ...prev, ...summaryMap }));
      } catch (e) {
        console.error("Error fetching team activities:", e);
      } finally {
        setActivityDataLoaded(true);
      }
    };
    fetchTeamActivities();
  }, [user?.id, isCoach]);

  // Fetch groups for coaches (to assign activities to)
  useEffect(() => {
    const fetchCoachGroups = async () => {
      if (!user?.id || !isCoach) return;
      try {
        const res = await axios.get(`${API}/groups?coach_id=${user.id}`);
        setCoachGroups(res.data);
      } catch (e) {
        console.error("Error fetching coach groups:", e);
      }
    };
    fetchCoachGroups();
  }, [user?.id, isCoach]);

  // Fetch coach's created activities
  useEffect(() => {
    const fetchCoachActivities = async () => {
      if (!user?.id || !isCoach) return;
      try {
        const res = await axios.get(`${API}/activities?coach_id=${user.id}`);
        setTeamActivities(res.data);
        const summaryMap = {};
        res.data.forEach((activity) => {
          summaryMap[`team_${activity.id}`] = {
            attending_count: activity.attendance_count || 0,
            total_athletes: activity.total_athletes || 0,
          };
        });
        setAttendanceSummary((prev) => ({ ...prev, ...summaryMap }));
      } catch (e) {
        console.error("Error fetching coach activities:", e);
      }
    };
    fetchCoachActivities();
  }, [user?.id, isCoach]);

  // Activity categories
  const activityCategories = [
    { value: 'team_training', label: 'Team Training', icon: Users },
    { value: 'specialist_training', label: 'Specialist Training', icon: Target },
    { value: 'strength_training', label: 'Strength Training', icon: Dumbbell },
    { value: 'conditioning_training', label: 'Conditioning Training', icon: Activity }
  ];

  // Get today's date for comparison
  const today = new Date();

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const closeAddActivityModal = () => {
    setShowAddActivity(false);
    setEditingActivity(null);
    setEditScope('single');
    setNewActivity(getDefaultActivityForm());
  };

  const getRawTeamActivityId = (activityId) => activityId?.startsWith('team_') ? activityId.replace('team_', '') : activityId;

  const isCustomOwner = (activity) => {
    if (!activity?.id?.startsWith('custom_')) return false;
    if (!user?.id) return false;
    return !activity.created_by || activity.created_by === user.id;
  };

  const editableCoachGroups = coachGroups.filter(group => !group.is_read_only);
  const isReadOnlyCoCoach = isCoach && coachGroups.length > 0 && editableCoachGroups.length === 0;

  const canEditActivity = (activity) => {
    if (!activity) return false;
    if (activity.isTeamActivity) {
      return Boolean(isCoach && user?.id && activity.coach_id === user.id);
    }
    return isCustomOwner(activity);
  };

  const canDeleteActivity = (activity) => canEditActivity(activity);

  const handleOpenEditActivity = (activity) => {
    if (!canEditActivity(activity)) {
      toast.error("You can only edit activities you created");
      return;
    }

    // If this is part of a recurring series with >1 occurrences, ask the coach
    // whether to edit only this occurrence or the whole series.
    if (activity.isTeamActivity && activity.series_id && getSeriesCount(activity) > 1) {
      setPendingEditActivity(activity);
      setEditScope('single');
      setShowEditScopeModal(true);
      return;
    }

    openEditFormForActivity(activity, 'single');
  };

  const openEditFormForActivity = (activity, scope) => {
    const activityDate = activity.date instanceof Date ? activity.date : new Date(activity.date);
    const formattedDate = activityDate.toISOString().split('T')[0];

    setEditScope(scope);
    setEditingActivity(activity);
    setNewActivity({
      title: activity.title || '',
      category: activity.type || 'team_training',
      date: formattedDate,
      time: activity.time || '18:00',
      duration: activity.duration || 90,
      location: activity.location && activity.location !== 'TBD' ? activity.location : '',
      selectedGroups: activity.isTeamActivity && activity.group_id ? [activity.group_id] : []
    });

    setShowActivityDetail(false);
    setSelectedActivity(null);
    setShowAddActivity(true);
  };

  const refreshCoachActivities = async () => {
    if (!user?.id || !isCoach) return;
    const res = await axios.get(`${API}/activities?coach_id=${user.id}`);
    setTeamActivities(res.data);
  };

  const getActivitySourceId = (activity) => {
    if (activity?.isFromSession) return activity.id;
    if (activity?.isTeamActivity) return getRawTeamActivityId(activity.id);
    return activity?.id;
  };

  const getActivityAthletes = (activity) => {
    if (!activity?.group_id) return teamAthletes;
    const group = coachGroups.find((entry) => entry.id === activity.group_id);
    const groupAthleteIds = group?.athlete_ids || [];
    return teamAthletes.filter((athlete) => groupAthleteIds.includes(athlete.id));
  };

  const syncAttendanceState = (activityId, attendanceMap = {}, attendingCount = 0, totalAthletes = 0, attendanceItems = null) => {
    setAttendance((prev) => ({
      ...prev,
      [activityId]: attendanceMap,
    }));
    setAttendanceSummary((prev) => ({
      ...prev,
      [activityId]: {
        attending_count: attendingCount,
        total_athletes: totalAthletes,
      },
    }));
    if (attendanceItems) {
      setAttendanceDetails((prev) => ({
        ...prev,
        [activityId]: attendanceItems,
      }));
    }
  };

  const fetchActivityAttendance = async (activity, viewerId = user?.id) => {
    if (!activity?.isTeamActivity || !viewerId) return null;

    const rawActivityId = getRawTeamActivityId(activity.id);
    const res = await axios.get(`${API}/activities/${rawActivityId}/attendance?viewer_id=${viewerId}`);

    if (res.data?.attendance_map) {
      syncAttendanceState(
        activity.id,
        res.data.attendance_map,
        res.data.attending_count || 0,
        res.data.total_athletes || 0,
        res.data.attendance || null,
      );
    }

    return res.data;
  };

  const updateActivityAttendance = async (activity, attending, athleteId = null) => {
    if (!activity?.isTeamActivity || !user?.id) return null;
    const rawActivityId = getRawTeamActivityId(activity.id);
    const res = await axios.put(`${API}/activities/${rawActivityId}/attendance`, {
      viewer_id: user.id,
      athlete_id: athleteId,
      attending,
    });

    if (res.data?.attendance_map) {
      syncAttendanceState(
        activity.id,
        res.data.attendance_map,
        res.data.attending_count || 0,
        res.data.total_athletes || 0,
        res.data.attendance || null,
      );
    }

    if (!isCoach) {
      setActivityAttendanceChoice(Boolean(attending));
    }

    return res.data;
  };

  const resolveSessionForActivity = (activity) => {
    if (activity.isFromSession) {
      return sessions.find(s => s.id === activity.id);
    }

    const rawActivityId = getActivitySourceId(activity);
    const matchBySource = sessions.find(s => s.source_activity_id === rawActivityId);
    if (matchBySource) return matchBySource;

    // Repeated trainings may share a title and even a date. A notification must
    // never open or overwrite the rating of another identified activity.
    return sessions.find(s => !s.source_activity_id && s.name === activity.title &&
      Math.abs(new Date(s.completed_at).getTime() - new Date(activity.date).getTime()) < 60000);
  };

  // Build activities from user sessions and custom activities only (no hardcoded dummy data)
  const sessionsAsActivities = sessions.map(session => {
    const sessionDate = session.completed_at ? new Date(session.completed_at) : new Date();
    const isPast = sessionDate < today;
    return {
      id: session.id,
      title: session.name,
      type: session.type || 'practice',
      date: sessionDate,
      time: sessionDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
      duration: session.duration || 60,
      location: session.notes?.includes('Location:') 
        ? session.notes.split('Location:')[1]?.split('|')[0]?.trim() || 'Training Ground'
        : 'Training Ground',
      isPast,
      canRate: isPast,
      isFromSession: true
    };
  });

  const customAsActivities = customActivities.map(activity => {
    // Parse date as local date to avoid timezone issues
    const [year, month, day] = activity.date.split('-').map(Number);
    const [hours, minutes] = (activity.time || '18:00').split(':').map(Number);
    const activityDate = new Date(year, month - 1, day, hours, minutes);
    const isPast = activityDate < today;
    return {
      ...activity,
      date: activityDate,
      isPast,
      canRate: isPast
    };
  });

  // Team activities from coach (for athletes in groups)
  const teamAsActivities = teamActivities.map(activity => {
    const activityDate = new Date(activity.scheduled_at);
    const isPast = activityDate < today;
    return {
      id: `team_${activity.id}`,
      title: activity.title,
      type: activity.activity_type || 'practice',
      date: activityDate,
      time: activityDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
      duration: activity.duration || 60,
      location: activity.location || 'TBD',
      isPast,
      canRate: isPast,
      isTeamActivity: true,
      group_id: activity.group_id,
      coach_id: activity.coach_id,
      groupName: activity.group_name,
      groupColor: activity.group_color,
      groupPhotoUrl: activity.group_photo_url,
      coachName: activity.coach_name,
      description: activity.description,
      is_read_only: Boolean(activity.is_read_only),
      series_id: activity.series_id,
      attendance_count: activity.attendance_count || 0,
      total_athletes: activity.total_athletes || 0,
    };
  });

  // Combine sessions, custom activities, and team activities
  const allActivities = [...sessionsAsActivities, ...customAsActivities, ...teamAsActivities]
    .filter((activity, index, self) => 
      index === self.findIndex(a => a.title === activity.title && a.date.toDateString() === activity.date.toDateString())
    )
    .sort((a, b) => a.date - b.date);

  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);

  const endOfCurrentWeek = new Date(todayStart);
  const daysUntilSunday = (7 - todayStart.getDay()) % 7;
  endOfCurrentWeek.setDate(todayStart.getDate() + daysUntilSunday);
  endOfCurrentWeek.setHours(23, 59, 59, 999);

  // Past activities remain visible in the agenda for 24 hours after they occurred
  // (applies to both athletes and coaches).
  const cutoff24h = new Date(today.getTime() - 24 * 60 * 60 * 1000);

  const recentPastActivities = [...allActivities]
    .filter(activity => activity.isPast && activity.date >= cutoff24h)
    .sort((a, b) => a.date - b.date);

  const athleteCurrentWeekActivities = allActivities.filter(activity => {
    if (activity.isPast) return false;
    return activity.date >= todayStart && activity.date <= endOfCurrentWeek;
  });

  const athleteFutureActivitiesBeyondWeek = allActivities.filter(activity => {
    if (activity.isPast) return false;
    return activity.date > endOfCurrentWeek;
  });

  const displayActivities = isAthlete
    ? [
        ...recentPastActivities,
        ...athleteCurrentWeekActivities,
        ...(showAllAthleteActivities ? athleteFutureActivitiesBeyondWeek : []),
      ]
    : allActivities.filter(activity => activity.date >= cutoff24h);

  const hasHiddenAthleteActivities = isAthlete && athleteFutureActivitiesBeyondWeek.length > 0;

  // Helper function to generate recurring dates
  // Uses noon (12:00) as base time to avoid DST issues
  const generateRecurringDates = (startDate, endDate, frequency) => {
    const dates = [];
    
    // Parse dates as local dates at NOON to avoid DST shift issues
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    
    // Use 12:00 noon as the base time - this prevents DST shifts from changing the date
    let current = new Date(startYear, startMonth - 1, startDay, 12, 0, 0);
    const end = new Date(endYear, endMonth - 1, endDay, 12, 0, 0);
    
    while (current <= end) {
      // Format as YYYY-MM-DD
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      
      if (frequency === 'daily') {
        current.setDate(current.getDate() + 1);
      } else if (frequency === 'weekly') {
        current.setDate(current.getDate() + 7);
      }
    }
    
    return dates;
  };

  // Save activity (create or edit)
  const handleSaveActivity = async () => {
    if (!newActivity.title.trim()) {
      toast.error("Please enter an activity title");
      return;
    }

    if (isReadOnlyCoCoach) {
      toast.error("You have read-only co-coach access for these groups");
      return;
    }

    // Validate recurring options
    if (newActivity.isRecurring && !newActivity.recurringEndDate) {
      toast.error("Please select an end date for recurring activities");
      return;
    }

    const categoryLabel = activityCategories.find(c => c.value === newActivity.category)?.label || newActivity.category;
    const activityDate = new Date(newActivity.date + 'T' + newActivity.time);
    const isPast = activityDate < today;

    if (editingActivity) {
      if (editingActivity.isTeamActivity) {
        if (!isCoach || !user?.id) {
          toast.error("Only coaches can edit group activities");
          return;
        }

        if (newActivity.selectedGroups.length !== 1) {
          toast.error("Please select exactly one group");
          return;
        }

        // Series edit: update all occurrences in the recurring series
        if (editScope === 'series' && editingActivity.series_id) {
          try {
            const res = await axios.put(
              `${API}/activities/series/${editingActivity.series_id}?coach_id=${user.id}`,
              {
                group_id: newActivity.selectedGroups[0],
                title: newActivity.title,
                description: newActivity.location ? `Location: ${newActivity.location}` : '',
                activity_type: newActivity.category,
                location: newActivity.location || '',
                // scheduled_at carries the new time-of-day; backend keeps each occurrence's date
                scheduled_at: new Date(newActivity.date + 'T' + newActivity.time).toISOString(),
                duration: parseInt(newActivity.duration) || 90,
              }
            );
            await refreshCoachActivities();
            toast.success(`${res.data?.updated_count || 'All'} activities in series updated`);
            closeAddActivityModal();
            return;
          } catch (e) {
            console.error("Error updating activity series:", e);
            toast.error("Failed to update activity series");
            return;
          }
        }

        try {
          await axios.put(`${API}/activities/${getRawTeamActivityId(editingActivity.id)}?coach_id=${user.id}`, {
            group_id: newActivity.selectedGroups[0],
            title: newActivity.title,
            description: newActivity.location ? `Location: ${newActivity.location}` : '',
            activity_type: newActivity.category,
            location: newActivity.location || '',
            scheduled_at: new Date(newActivity.date + 'T' + newActivity.time).toISOString(),
            duration: parseInt(newActivity.duration) || 90
          });
          await refreshCoachActivities();
          toast.success("Activity updated");
          closeAddActivityModal();
          return;
        } catch (e) {
          console.error("Error updating team activity:", e);
          toast.error("Failed to update activity");
          return;
        }
      }

      if (editingActivity.id?.startsWith('custom_')) {
        const updatedActivities = customActivities.map((activity) => {
          if (activity.id !== editingActivity.id) return activity;
          return {
            ...activity,
            title: newActivity.title,
            type: newActivity.category,
            categoryLabel,
            date: newActivity.date,
            time: newActivity.time,
            duration: parseInt(newActivity.duration) || 90,
            location: newActivity.location || 'TBD'
          };
        });
        setCustomActivities(updatedActivities);
        localStorage.setItem('mygoal-custom-activities', JSON.stringify(updatedActivities));
        window.dispatchEvent(new Event('mygoal-activities-updated'));
        toast.success("Activity updated");
        closeAddActivityModal();
        return;
      }
    }
    
    if (isCoach && newActivity.selectedGroups.length > 0) {
      try {
        // Generate dates for recurring activities or use single date
        const dates = newActivity.isRecurring && newActivity.recurringEndDate
          ? generateRecurringDates(newActivity.date, newActivity.recurringEndDate, newActivity.recurringFrequency)
          : [newActivity.date];
        
        // Create a series_id for recurring team activities
        const seriesId = dates.length > 1 ? `series_${Date.now()}` : null;
        
        let createdCount = 0;
        for (const date of dates) {
          const scheduledAt = new Date(date + 'T' + newActivity.time).toISOString();
          for (const groupId of newActivity.selectedGroups) {
            await axios.post(`${API}/activities?coach_id=${user.id}`, {
              group_id: groupId,
              title: newActivity.title,
              description: newActivity.location ? `Location: ${newActivity.location}` : '',
              activity_type: newActivity.category,
              location: newActivity.location || '',
              scheduled_at: scheduledAt,
              duration: parseInt(newActivity.duration) || 90,
              series_id: seriesId  // Link activities in a recurring series
            });
            createdCount++;
          }
        }

        await refreshCoachActivities();

        const groupNames = newActivity.selectedGroups
          .map(gid => editableCoachGroups.find(g => g.id === gid)?.name)
          .filter(Boolean)
          .join(', ');
        
        if (dates.length > 1) {
          toast.success(`${dates.length} activities created for: ${groupNames}`);
        } else {
          toast.success(`Activity created for: ${groupNames}`);
        }
      } catch (e) {
        console.error("Error creating team activity:", e);
        toast.error("Failed to create activity");
        return;
      }
    } else {
      // Generate dates for recurring activities or use single date
      const dates = newActivity.isRecurring && newActivity.recurringEndDate
        ? generateRecurringDates(newActivity.date, newActivity.recurringEndDate, newActivity.recurringFrequency)
        : [newActivity.date];
      
      // Create a series ID for recurring activities so they can be deleted together
      const seriesId = dates.length > 1 ? `series_${Date.now()}` : null;
      
      const newActivitiesToAdd = dates.map((date, index) => ({
        id: `custom_${Date.now()}_${index}`,
        title: newActivity.title,
        type: newActivity.category,
        categoryLabel,
        date: date,
        time: newActivity.time,
        duration: parseInt(newActivity.duration) || 90,
        location: newActivity.location || 'TBD',
        created_by: user?.id || '',
        created_by_role: isCoach ? 'coach' : 'athlete',
        series_id: seriesId // Link activities in a recurring series
      }));

      const updatedActivities = [...customActivities, ...newActivitiesToAdd];
      setCustomActivities(updatedActivities);
      localStorage.setItem('mygoal-custom-activities', JSON.stringify(updatedActivities));
      window.dispatchEvent(new Event('mygoal-activities-updated'));
      
      if (dates.length > 1) {
        toast.success(`${dates.length} activities "${newActivity.title}" added!`);
      } else {
        toast.success(`Activity "${newActivity.title}" added!`);
      }
    }
    
    closeAddActivityModal();
  };

  const handleDeleteActivity = async (activity) => {
    if (!canDeleteActivity(activity)) {
      toast.error("You can only delete activities you created");
      return;
    }

    if (activity.isTeamActivity && isCoach && user?.id) {
      try {
        await axios.delete(`${API}/activities/${getRawTeamActivityId(activity.id)}?coach_id=${user.id}`);
        await refreshCoachActivities();
        toast.success("Activity deleted");
      } catch (e) {
        console.error("Error deleting team activity:", e);
        toast.error("Failed to delete activity");
        return;
      }
    } else {
      const updatedActivities = customActivities.filter(a => a.id !== activity.id);
      setCustomActivities(updatedActivities);
      localStorage.setItem('mygoal-custom-activities', JSON.stringify(updatedActivities));
      window.dispatchEvent(new Event('mygoal-activities-updated'));
      toast.success("Activity deleted");
    }

    if (selectedActivity?.id === activity.id) {
      setShowActivityDetail(false);
      setSelectedActivity(null);
    }
  };

  // Delete all activities in a series
  const handleDeleteSeries = async (activity) => {
    if (!canDeleteActivity(activity)) {
      toast.error("You can only delete activities you created");
      return;
    }

    const seriesId = activity.series_id;
    if (!seriesId) {
      // Single activity, just delete it
      handleDeleteActivity(activity);
      return;
    }

    // Check if this is a team activity (from database) or custom activity (localStorage)
    if (activity.isTeamActivity && isCoach && user?.id) {
      // Team activity - use backend API to delete series
      try {
        // Get count first
        const countRes = await axios.get(`${API}/activities/series/${seriesId}/count?coach_id=${user.id}`);
        const count = countRes.data.count;

        if (!window.confirm(`Delete all ${count} activities in this series?`)) {
          return;
        }

        const res = await axios.delete(`${API}/activities/series/${seriesId}?coach_id=${user.id}`);
        await refreshCoachActivities();
        toast.success(`${res.data.deleted_count} activities deleted`);
      } catch (e) {
        console.error("Error deleting series:", e);
        toast.error("Failed to delete activity series");
        return;
      }
    } else {
      // Custom activity - use localStorage
      const seriesActivities = customActivities.filter(a => a.series_id === seriesId);
      const count = seriesActivities.length;

      if (!window.confirm(`Delete all ${count} activities in this series?`)) {
        return;
      }

      const updatedActivities = customActivities.filter(a => a.series_id !== seriesId);
      setCustomActivities(updatedActivities);
      localStorage.setItem('mygoal-custom-activities', JSON.stringify(updatedActivities));
      window.dispatchEvent(new Event('mygoal-activities-updated'));
      toast.success(`${count} activities deleted`);
    }

    if (selectedActivity?.series_id === seriesId) {
      setShowActivityDetail(false);
      setSelectedActivity(null);
    }
  };

  // Get count of activities in the same series
  const getSeriesCount = (activity) => {
    if (!activity?.series_id) return 0;
    
    // For team activities, count from teamActivities
    if (activity.isTeamActivity) {
      return teamActivities.filter(a => a.series_id === activity.series_id).length;
    }
    
    // For custom activities, count from customActivities
    return customActivities.filter(a => a.series_id === activity.series_id).length;
  };


  // Coach: Toggle attendance for an athlete
  const toggleAttendance = async (activity, athleteId) => {
    const currentValue = attendance[activity.id]?.[athleteId] ?? true;
    await updateActivityAttendance(activity, !currentValue, athleteId);
  };

  // Coach: Get attendance count for an activity
  const getAttendanceCount = (activity) => {
    const summary = attendanceSummary[activity.id];
    if (summary) return summary.attending_count;
    return activity.attendance_count || 0;
  };

  // Coach: Handle activity click - show detail modal and fetch activity goals
  const handleCoachActivityClick = async (activity) => {
    setSelectedActivity(activity);
    setShowActivityDetail(true);
    
    if (user?.id) {
      try {
        const [goalsRes] = await Promise.all([
          axios.get(`${API}/coach/team/activity-goals?coach_id=${user.id}&activity_id=${activity.id}`),
          fetchActivityAttendance(activity, user.id),
        ]);
        setActivityGoalsFromDb(goalsRes.data);
      } catch (e) {
        console.error("Error fetching activity goals:", e);
        setActivityGoalsFromDb([]);
      }
    }
  };

  // Fetch sessions and goals
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      try {
        if (!isCoach) {
          await repairAthleteSessionTimestamps(user.id);
        }
        const [sessionsRes, goalsRes] = await Promise.all([
          axios.get(`${API}/sessions?user_id=${user.id}`),
          axios.get(`${API}/goals?user_id=${user.id}`)
        ]);
        setSessions(sessionsRes.data);
        setGoals(goalsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      } finally {
        setPersonalDataLoaded(true);
      }
    };
    fetchData();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Check if an activity has a session
  const getSessionForActivity = (activity) => {
    return resolveSessionForActivity(activity);
  };

  const assignedGoalsFor = (activity) => {
    if (!activity) return [];
    const sourceId = getActivitySourceId(activity);
    const linked = goals.filter(goal => [activity.id, sourceId, `team_${sourceId}`].includes(goal.activity_id));
    return linked.length ? linked : (activityGoals[activity.id] || []);
  };

  // Handle clicking on past activity to rate
  const handleActivityClick = (activity) => {
    if (activity.canRate) {
      setSelectedActivity(activity);
      const existingSession = getSessionForActivity(activity);
      const assignedGoals = assignedGoalsFor(activity);
      
      if (existingSession?.rating) {
        setRatings({
          fun: existingSession.rating.fun || 5,
          learning: existingSession.rating.learning || 5,
          mygoal: existingSession.rating.mygoal || 5,
          intensity: existingSession.rating.intensity || 5
        });
      } else {
        setRatings({ fun: 5, learning: 5, mygoal: 5, intensity: 5 });
      }
      
      const initialGoalRatings = {};
      assignedGoals.forEach(g => {
        const storedGoal = goals.find(goal => goal.id === g.id) || g;
        const previousRatings = (storedGoal.ratings || []).filter(rating => rating.session_name === activity.title);
        initialGoalRatings[g.id] = existingSession?.rating
          ? previousRatings[previousRatings.length - 1]?.rating ?? existingSession.rating.mygoal ?? 5
          : 5;
      });
      setGoalRatings(initialGoalRatings);
      
      setShowRating(true);
    }
  };

  // Handle clicking on future activity to set goals
  const handleFutureActivityClick = (activity) => {
    if (!activity.isPast) {
      setSelectedActivity(activity);
      setSelectedGoalsForActivity(assignedGoalsFor(activity));
      setActivityAttendanceChoice(true);

      if (activity.isTeamActivity && user?.id) {
        fetchActivityAttendance(activity, user.id)
          .then((data) => {
            if (typeof data?.current_attending === 'boolean') {
              setActivityAttendanceChoice(data.current_attending);
            }
          })
          .catch((error) => {
            console.error("Error fetching athlete attendance:", error);
          });
      }

      setShowGoalSelection(true);
    }
  };

  // A push can arrive while the app is closed, so wait for both agenda and
  // personal data before opening its exact activity (never a same-name match).
  useEffect(() => {
    if (!isAthlete || !activityDataLoaded || !personalDataLoaded) return;
    const params = new URLSearchParams(location.search);
    const id = params.get('activity_id');
    const action = params.get('action');
    const key = `${user?.id}:${location.search}`;
    if (!id || !['goal', 'rating'].includes(action) || handledReminder.current === key) return;
    handledReminder.current = key;
    const activity = [...teamAsActivities, ...customAsActivities].find(a => getActivitySourceId(a) === id);
    if (!activity) {
      toast.error('This activity is no longer available. Please check your agenda.');
      return;
    }
    if (action === 'goal' && !activity.isPast) handleFutureActivityClick(activity);
    else if (activity.canRate) handleActivityClick(activity);
    else toast.info('This activity has not finished yet. You can rate it afterwards.');
    // Handlers use the current state; the reminder is deliberately opened once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, activityDataLoaded, personalDataLoaded, teamActivities, customActivities, sessions, goals, isAthlete, user?.id]);

  // Only confirm a goal once its association has actually reached the server.
  const handleSaveGoalSelection = async () => {
    if (!selectedActivity || !user?.id || saving) return;
    setSaving(true);
    try {
      const sourceId = getActivitySourceId(selectedActivity);
      const selectedGoal = selectedGoalsForActivity[0];
      const previous = assignedGoalsFor(selectedActivity);
      const updatedGoals = [...goals];
      if (selectedGoal) {
        const response = await axios.put(`${API}/goals/${selectedGoal.id}`, {
          activity_id: sourceId, activity_name: selectedActivity.title
        });
        const index = updatedGoals.findIndex(goal => goal.id === selectedGoal.id);
        if (index >= 0) updatedGoals[index] = response.data;
      }
      for (const goal of previous.filter(goal => goal.id !== selectedGoal?.id)) {
        const response = await axios.put(`${API}/goals/${goal.id}`, { activity_id: null, activity_name: null });
        const index = updatedGoals.findIndex(item => item.id === goal.id);
        if (index >= 0) updatedGoals[index] = response.data;
      }
      if (selectedActivity.isTeamActivity) {
        await updateActivityAttendance(selectedActivity, activityAttendanceChoice);
      }
      setGoals(updatedGoals);
      const newActivityGoals = { ...activityGoals, [selectedActivity.id]: selectedGoalsForActivity };
      setActivityGoals(newActivityGoals);
      localStorage.setItem('mygoal-activity-goals', JSON.stringify(newActivityGoals));
      window.dispatchEvent(new Event('mygoal-activities-updated'));
      toast.success(selectedGoal ? 'Goal set for this activity!' : 'Activity choice saved!');
      setShowGoalSelection(false);
      setSelectedActivity(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save your activity choice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle goal selection
  const toggleGoalSelection = (goal) => {
    setSelectedGoalsForActivity(prev => {
      const isSelected = prev.some(g => g.id === goal.id);
      if (isSelected) {
        return [];
      } else {
        return [goal];
      }
    });
  };

  const handleSaveRating = async () => {
    if (!selectedActivity || !user?.id) return;
    if (messageRef.current && !messageRef.current.validate()) return;
    setSaving(true);

    try {
      const existingSession = getSessionForActivity(selectedActivity);
      let sessionId = existingSession?.id;

      if (!sessionId) {
        // Create new session with source_activity_id for proper linking
        const rawActivityId = getActivitySourceId(selectedActivity);
        
        const newSession = await axios.post(`${API}/sessions`, {
          name: selectedActivity.title,
          type: selectedActivity.type,
          duration: selectedActivity.duration,
          calories: Math.round(selectedActivity.duration * 8),
          notes: `Location: ${selectedActivity.location}`,
          user_id: user.id,
          source_activity_id: rawActivityId,
          completed_at: selectedActivity.date.toISOString(),
        });
        sessionId = newSession.data.id;
        
        // Refresh sessions so we have the new one cached
        const sessionsRes = await axios.get(`${API}/sessions?user_id=${user.id}`);
        setSessions(sessionsRes.data);
      }

      await axios.post(`${API}/sessions/${sessionId}/rate`, currentRatings);
      await messageRef.current?.send(sessionId);
      const rawActivityId = getActivitySourceId(selectedActivity);
      await ActivityReminderService.cancelActivityRatingReminder(user.id, rawActivityId);
      
      const assignedGoals = assignedGoalsFor(selectedActivity);
      for (const goal of assignedGoals) {
        if (goalRatings[goal.id]) {
          await axios.post(`${API}/goals/${goal.id}/rate`, { 
            rating: goalRatings[goal.id],
            session_name: selectedActivity.title
          });
        }
      }
      
      toast.success("Training rated successfully!");
      setShowRating(false);
      setSelectedActivity(null);
      setGoalRatings({});
      
      const res = await axios.get(`${API}/sessions?user_id=${user.id}`);
      setSessions(res.data);
    } catch (e) {
      console.error("Error saving rating:", e);
      if (e.message !== "MESSAGE_SEND_FAILED") toast.error("Failed to save rating");
    } finally {
      setSaving(false);
    }
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'match': return Target;
      case 'strength': 
      case 'strength_training': return Dumbbell;
      case 'cardio':
      case 'conditioning_training': return Activity;
      case 'team_training': return Users;
      case 'specialist_training': return Target;
      default: return Activity;
    }
  };

  const ratingTopics = [
    { key: 'fun', label: 'Fun', question: { en: 'Did I enjoy today?', nl: 'Heb ik vandaag genoten?' }, tooltip: { en: 'Did this session feel enjoyable and motivating?', nl: 'Voelde deze sessie leuk en motiverend?' }, icon: Smile, color: 'text-yellow-500' },
    { key: 'learning', label: 'Learning', question: { en: 'Did I grow today?', nl: 'Ben ik vandaag gegroeid?' }, tooltip: { en: 'Did I learn or improve a skill today?', nl: 'Heb ik vandaag een vaardigheid geleerd of verbeterd?' }, icon: BookOpen, color: 'text-blue-500' },
    { key: 'mygoal', label: 'MyGoal', question: { en: 'Did I develop MyGoal?', nl: 'Heb ik mijn MyGoal ontwikkeld?' }, tooltip: { en: 'Did this session move me forward on MyGoal?', nl: 'Heeft deze sessie mij vooruit geholpen op mijn MyGoal?' }, icon: Target, color: 'text-purple-500' },
    { key: 'intensity', label: 'Intensity', question: { en: 'Did I push myself?', nl: 'Heb ik mezelf uitgedaagd?' }, tooltip: { en: 'Did I train with enough effort and focus?', nl: 'Trainde ik met voldoende inzet en focus?' }, icon: Gauge, color: 'text-red-500' }
  ];

  const assignedGoalScores = assignedGoalsFor(selectedActivity).map(goal => goalRatings[goal.id] ?? 5);
  const currentRatings = {
    ...ratings,
    mygoal: assignedGoalScores.length ? Number((assignedGoalScores.reduce((sum, value) => sum + value, 0) / assignedGoalScores.length).toFixed(1)) : ratings.mygoal,
  };

  const radarData = [
    { subject: 'Fun', value: ratings.fun, fullMark: 10 },
    { subject: 'Learning', value: ratings.learning, fullMark: 10 },
    { subject: 'MyGoal', value: currentRatings.mygoal, fullMark: 10 },
    { subject: 'Intensity', value: ratings.intensity, fullMark: 10 }
  ];

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-agenda">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>{isCoach ? 'Upcoming Sessions' : 'Agenda'}</h1>
        {!isReadOnlyCoCoach ? (
          <button 
            data-testid="add-activity-button"
            onClick={() => {
              setEditingActivity(null);
              setNewActivity(getDefaultActivityForm());
              setShowAddActivity(true);
            }} 
            className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-r from-[#493986] to-[#92a7e8]"
          >
            <Plus className="w-5 h-5 text-white" />
          </button>
        ) : (
          <div className="w-10 h-10" data-testid="add-activity-button-disabled" />
        )}
      </header>

      {isReadOnlyCoCoach && (
        <div className="px-4 mb-2" data-testid="agenda-readonly-cocoach-banner">
          <div className={`p-2.5 rounded-xl border text-xs ${isDark ? 'bg-white/5 border-white/10 text-white/80' : 'bg-[#493986]/5 border-[#493986]/15 text-[#493986]/80'}`}>
            You have read-only co-coach access on assigned groups.
          </div>
        </div>
      )}

      {/* Add Activity Modal */}
      {showAddActivity && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeAddActivityModal} />
          <div 
            className={`relative w-full max-h-[90vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4 pb-8`}
            style={{ 
              transform: `translateY(${addActivitySwipe.translateY}px)`,
              transition: addActivitySwipe.isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            onTouchStart={addActivitySwipe.onTouchStart}
            onTouchMove={addActivitySwipe.onTouchMove}
            onTouchEnd={addActivitySwipe.onTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            
            <h2 className={`text-xl font-bold mb-4 ${theme.textPrimary}`}>
              {editingActivity
                ? (editScope === 'series'
                    ? `Edit Series (${getSeriesCount(editingActivity)} activities)`
                    : 'Edit Activity')
                : 'Add Activity'}
            </h2>
            {editingActivity && editScope === 'series' && (
              <p className={`text-xs mb-4 -mt-2 ${theme.textMuted}`} data-testid="edit-series-hint">
                Changes apply to all {getSeriesCount(editingActivity)} occurrences. Each occurrence keeps its own date — only the time-of-day below is applied to all.
              </p>
            )}
            
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Activity Name</label>
              <input 
                type="text" 
                placeholder="e.g., Morning Training"
                value={newActivity.title}
                onChange={(e) => setNewActivity({...newActivity, title: e.target.value})}
                className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                data-testid="activity-title-input"
              />
            </div>

            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Category</label>
              <div className="grid grid-cols-2 gap-2">
                {activityCategories.map(cat => {
                  const Icon = cat.icon;
                  const isSelected = newActivity.category === cat.value;
                  return (
                    <button
                      key={cat.value}
                      onClick={() => setNewActivity({...newActivity, category: cat.value})}
                      className={`p-3 rounded-xl border flex items-center gap-2 transition-all ${
                        isSelected 
                          ? 'bg-gradient-to-r from-[#493986] to-[#92a7e8] border-transparent text-white' 
                          : `${theme.card} border ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`
                      }`}
                      data-testid={`category-${cat.value}`}
                    >
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : theme.textPrimary}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-white' : theme.textPrimary}`}>{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
                  Date {editingActivity && editScope === 'series' && <span className="text-xs">(per-occurrence — not editable)</span>}
                </label>
                <input
                  type="date"
                  value={newActivity.date}
                  disabled={editingActivity && editScope === 'series'}
                  onChange={(e) => setNewActivity({...newActivity, date: e.target.value})}
                  className={`w-full px-4 py-3 rounded-xl border ${theme.input} ${editingActivity && editScope === 'series' ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="activity-date-input"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Time</label>
                <input 
                  type="time" 
                  value={newActivity.time}
                  onChange={(e) => setNewActivity({...newActivity, time: e.target.value})}
                  className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                  data-testid="activity-time-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Duration (min)</label>
                <input 
                  type="number" 
                  value={newActivity.duration}
                  onChange={(e) => setNewActivity({...newActivity, duration: e.target.value})}
                  className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                  data-testid="activity-duration-input"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Location</label>
                <input 
                  type="text" 
                  placeholder="e.g., Main Field"
                  value={newActivity.location}
                  onChange={(e) => setNewActivity({...newActivity, location: e.target.value})}
                  className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                  data-testid="activity-location-input"
                />
              </div>
            </div>

            {/* Recurring Options - Only for new activities (not editing) */}
            {!editingActivity && isCoach && (
              <div className="mb-4">
                <div 
                  onClick={() => setNewActivity({...newActivity, isRecurring: !newActivity.isRecurring})}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    newActivity.isRecurring 
                      ? 'bg-gradient-to-r from-[#493986]/10 to-[#92a7e8]/10 border-[#493986]/30' 
                      : `${theme.card} border ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`
                  }`}
                  data-testid="recurring-toggle"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    newActivity.isRecurring ? 'bg-[#493986]' : isDark ? 'bg-white/10' : 'bg-[#493986]/10'
                  }`}>
                    <Repeat className={`w-5 h-5 ${newActivity.isRecurring ? 'text-white' : theme.textPrimary}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${theme.textPrimary}`}>Repeat Activity</p>
                    <p className={`text-xs ${theme.textMuted}`}>Schedule recurring sessions</p>
                  </div>
                  <div className={`w-12 h-6 rounded-full transition-all ${
                    newActivity.isRecurring ? 'bg-[#493986]' : isDark ? 'bg-white/20' : 'bg-[#493986]/20'
                  }`}>
                    <div className={`w-5 h-5 mt-0.5 rounded-full bg-white shadow transition-all ${
                      newActivity.isRecurring ? 'ml-6' : 'ml-0.5'
                    }`} />
                  </div>
                </div>

                {newActivity.isRecurring && (
                  <div className={`mt-3 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`}>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className={`block text-xs font-medium mb-2 ${theme.textSecondary}`}>Frequency</label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setNewActivity({...newActivity, recurringFrequency: 'weekly'})}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                              newActivity.recurringFrequency === 'weekly'
                                ? 'bg-[#493986] text-white'
                                : `${isDark ? 'bg-white/10 text-white/70' : 'bg-white text-[#493986]/70'}`
                            }`}
                            data-testid="frequency-weekly"
                          >
                            Weekly
                          </button>
                          <button
                            onClick={() => setNewActivity({...newActivity, recurringFrequency: 'daily'})}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                              newActivity.recurringFrequency === 'daily'
                                ? 'bg-[#493986] text-white'
                                : `${isDark ? 'bg-white/10 text-white/70' : 'bg-white text-[#493986]/70'}`
                            }`}
                            data-testid="frequency-daily"
                          >
                            Daily
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className={`block text-xs font-medium mb-2 ${theme.textSecondary}`}>Until Date</label>
                        <input 
                          type="date" 
                          value={newActivity.recurringEndDate}
                          min={newActivity.date}
                          onChange={(e) => setNewActivity({...newActivity, recurringEndDate: e.target.value})}
                          className={`w-full px-3 py-2 rounded-lg border text-sm ${theme.input}`}
                          data-testid="recurring-end-date"
                        />
                      </div>
                    </div>
                    {newActivity.recurringEndDate && (
                      <p className={`text-xs ${theme.textMuted}`}>
                        This will create {
                          generateRecurringDates(newActivity.date, newActivity.recurringEndDate, newActivity.recurringFrequency).length
                        } activities ({newActivity.recurringFrequency === 'weekly' ? 'every week' : 'every day'} until {new Date(newActivity.recurringEndDate).toLocaleDateString()})
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Group Selection for Coaches */}
            {isCoach && editableCoachGroups.length > 0 && (!editingActivity || editingActivity.isTeamActivity) && (
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
                  {editingActivity?.isTeamActivity ? 'Assign to Group' : 'Assign to Groups'} <span className={`text-xs ${theme.textMuted}`}>{editingActivity?.isTeamActivity ? '(required for coach group activity)' : '(optional - visible to athletes in these groups)'}</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {editableCoachGroups.map(group => {
                    const isSelected = newActivity.selectedGroups.includes(group.id);
                    return (
                      <button
                        key={group.id}
                        onClick={() => {
                          const newSelected = editingActivity?.isTeamActivity
                            ? (isSelected ? [] : [group.id])
                            : (isSelected
                                ? newActivity.selectedGroups.filter(id => id !== group.id)
                                : [...newActivity.selectedGroups, group.id]);
                          setNewActivity({...newActivity, selectedGroups: newSelected});
                        }}
                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                          isSelected 
                            ? 'ring-2 ring-offset-2' 
                            : 'opacity-60 hover:opacity-100'
                        }`}
                        style={{ 
                          backgroundColor: `${group.color}20`, 
                          color: group.color,
                          borderColor: group.color,
                          ...(isSelected ? { ringColor: group.color } : {})
                        }}
                        data-testid={`group-select-${group.id}`}
                      >
                        <span 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: group.color }}
                        />
                        {group.name}
                        <span className={`text-xs ${isSelected ? '' : 'opacity-60'}`}>
                          ({group.athlete_ids?.length || 0})
                        </span>
                        {isSelected && (
                          <span className="ml-1">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {newActivity.selectedGroups.length > 0 && (
                  <p className={`text-xs mt-2 ${theme.textMuted}`}>
                    Activity will be visible to {newActivity.selectedGroups.reduce((acc, gid) => {
                      const group = editableCoachGroups.find(g => g.id === gid);
                      return acc + (group?.athlete_ids?.length || 0);
                    }, 0)} athletes
                  </p>
                )}
              </div>
            )}

            {isCoach && editableCoachGroups.length === 0 && (!editingActivity || editingActivity.isTeamActivity) && (
              <div className={`mb-6 p-3 rounded-xl ${isDark ? 'bg-yellow-500/10' : 'bg-yellow-50'} border ${isDark ? 'border-yellow-500/20' : 'border-yellow-200'}`}>
                <p className={`text-sm ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  💡 Create groups in "My Team" to assign activities to athletes
                </p>
              </div>
            )}

            <button
              onClick={handleSaveActivity}
              className="w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl"
              data-testid="save-activity-button"
            >
              {editingActivity
                ? (editingActivity.isTeamActivity
                    ? (editScope === 'series' ? `Update All ${getSeriesCount(editingActivity)} in Series` : 'Update Group Activity')
                    : 'Update Activity')
                : (isCoach && newActivity.selectedGroups.length > 0
                    ? `Create for ${newActivity.selectedGroups.length} Group${newActivity.selectedGroups.length > 1 ? 's' : ''}`
                    : 'Add Activity')
              }
            </button>
          </div>
        </div>
      )}

      {/* Goal Selection Modal for Future Activities */}
      {showGoalSelection && selectedActivity && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowGoalSelection(false)} />
          <div 
            className={`relative w-full max-h-[85vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4 pb-8`}
            style={{ 
              transform: `translateY(${goalSelectionSwipe.translateY}px)`,
              transition: goalSelectionSwipe.isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            onTouchStart={goalSelectionSwipe.onTouchStart}
            onTouchMove={goalSelectionSwipe.onTouchMove}
            onTouchEnd={goalSelectionSwipe.onTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            
            <h2 className={`text-xl font-bold mb-2 ${theme.textPrimary}`}>Set Goal for Training</h2>
            <p className={`text-sm mb-4 ${theme.textMuted}`}>
              {selectedActivity.title} • {formatDate(selectedActivity.date)}
            </p>
            
            <p className={`text-sm mb-4 ${theme.textSecondary}`}>
              Select 1 goal to focus on during this training. You'll rate it after the session.
            </p>
            
            {goals.length === 0 ? (
              <div className={`text-center py-8 ${theme.textMuted}`}>
                <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No goals created yet.</p>
                <button 
                  onClick={() => { setShowGoalSelection(false); navigate(`/mygoal?activity_id=${encodeURIComponent(getActivitySourceId(selectedActivity))}&activity_name=${encodeURIComponent(selectedActivity.title)}`); }}
                  className="mt-3 px-4 py-2 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white rounded-xl text-sm"
                >
                  Create Goals
                </button>
              </div>
            ) : (
              <div className="space-y-2 mb-6">
                {goals.map(goal => {
                  const isSelected = selectedGoalsForActivity.some(g => g.id === goal.id);
                  const CategoryIcon = categoryIcons[goal.category] || Target;
                  
                  return (
                    <div
                      key={goal.id}
                      onClick={() => toggleGoalSelection(goal)}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-gradient-to-r from-[#493986] to-[#92a7e8] border-transparent' 
                          : `${theme.card} border ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-white/20' : 'bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20'
                        }`}>
                          {isSelected ? (
                            <CheckCircle2 className="w-5 h-5 text-white" />
                          ) : (
                            <CategoryIcon className={`w-5 h-5 ${theme.textPrimary}`} />
                          )}
                        </div>
                        <div>
                          <h3 className={`font-semibold ${isSelected ? 'text-white' : theme.textPrimary}`}>{goal.title}</h3>
                          <p className={`text-xs ${isSelected ? 'text-white/70' : theme.textMuted}`}>{goal.category}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedActivity?.isTeamActivity && (
              <label
                className={`mt-4 flex items-center justify-between gap-3 p-3 rounded-xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-[#493986]/10 bg-[#493986]/5'}`}
                data-testid="goal-selection-attendance-checkbox"
              >
                <div>
                  <p className={`text-sm font-medium ${theme.textPrimary}`}>Present</p>
                  <p className={`text-xs ${theme.textMuted}`}>Saved together with this activity choice.</p>
                </div>
                <input
                  type="checkbox"
                  checked={activityAttendanceChoice}
                  onChange={(e) => setActivityAttendanceChoice(e.target.checked)}
                  className="h-4 w-4 accent-[#493986]"
                  data-testid="goal-selection-attendance-input"
                />
              </label>
            )}
            
            {goals.length > 0 && (
              <button
                onClick={handleSaveGoalSelection}
                disabled={saving}
                data-testid="save-activity-goal-button"
                className="disabled:opacity-50 w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl"
              >
                {selectedGoalsForActivity.length > 0 
                  ? `Set Goal: ${selectedGoalsForActivity[0].title}` 
                  : 'Save Without Goal'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Rating Modal for Past Activities */}
      {showRating && selectedActivity && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowRating(false)} />
          <div 
            className={`relative w-full max-h-[90vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4 pb-8`}
            style={{ 
              transform: `translateY(${ratingSwipe.translateY}px)`,
              transition: ratingSwipe.isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            onTouchStart={ratingSwipe.onTouchStart}
            onTouchMove={ratingSwipe.onTouchMove}
            onTouchEnd={ratingSwipe.onTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            
            <h2 className={`text-xl font-bold mb-4 ${theme.textPrimary}`}>Rate: {selectedActivity.title}</h2>
            
            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center">
                  <Calendar className={`w-6 h-6 ${theme.textPrimary}`} />
                </div>
                <div>
                  <h3 className={`font-semibold ${theme.textPrimary}`}>{selectedActivity.title}</h3>
                  <p className={`text-sm ${theme.textMuted}`}>
                    {formatDate(selectedActivity.date)} • {selectedActivity.time} • {selectedActivity.duration} min
                  </p>
                </div>
              </div>
            </div>

            <RatingMessageField key={selectedActivity.id} ref={messageRef} theme={theme} disabled={saving} sourceActivityId={getActivitySourceId(selectedActivity)} sessionId={getSessionForActivity(selectedActivity)?.id} />

            {/* Rating sliders FIRST */}
            <div className="space-y-3 mb-4">
              {ratingTopics.filter(t => t.key !== 'mygoal').map(topic => (
                <RatingSlider
                  key={topic.key}
                  value={ratings[topic.key]}
                  onChange={(val) => setRatings({...ratings, [topic.key]: val})}
                  label={topic.question.en}
                  infoText={topic.tooltip.en}
                  infoKey={topic.key}
                  testIdPrefix="agenda-rating"
                  disabled={saving}
                  icon={topic.icon}
                  color={topic.color}
                  isDark={isDark}
                />
              ))}
            </div>

            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-purple-500" />
                <h3 className={`font-semibold ${theme.textPrimary}`}>Did I develop MyGoal?</h3>
              </div>
              
              {(() => {
                const assignedGoals = assignedGoalsFor(selectedActivity);
                
                if (assignedGoals.length === 0) {
                  return (
                    <div>
                      <p className={`text-sm mb-3 ${theme.textMuted}`}>No goals were set for this training. Rate your overall goal progress:</p>
                      <RatingSlider
                        value={ratings.mygoal}
                        onChange={(val) => setRatings({...ratings, mygoal: val})}
                        label="Did I develop MyGoal?"
                        infoText="Did this session move me forward on MyGoal?"
                        infoKey="mygoal"
                        testIdPrefix="agenda-rating"
                        disabled={saving}
                        icon={Target}
                        color="text-purple-500"
                        isDark={isDark}
                      />
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-3">
                    <p className={`text-sm ${theme.textMuted}`}>Rate your progress on each goal:</p>
                    {assignedGoals.map(goal => {
                      const CategoryIcon = categoryIcons[goal.category] || Target;
                      return (
                        <RatingSlider
                          key={goal.id}
                          value={goalRatings[goal.id] ?? 5}
                          onChange={value => setGoalRatings(current => ({ ...current, [goal.id]: value }))}
                          label={goal.title}
                          icon={CategoryIcon}
                          color="text-purple-500"
                          isDark={isDark}
                          disabled={saving}
                        />
                      );
                    })}
                    
                    <div className={`pt-3 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
                      <p className={`text-xs ${theme.textMuted} mb-2`}>Overall MyGoal score (auto-calculated):</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-purple-500/20">
                          <div 
                            className="h-full rounded-full bg-purple-500 transition-all"
                            style={{ 
                              width: `${(currentRatings.mygoal / 10) * 100}%`
                            }}
                          />
                        </div>
                        <span className={`text-lg font-bold text-purple-500`}>
                          {formatRating(currentRatings.mygoal)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Radar chart preview BELOW sliders */}
            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>Rating Preview</h3>
              <ResponsiveContainer width="100%" height={180}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(73,57,134,0.1)'} />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(73,57,134,0.6)', fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 10]} tick={{ fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(73,57,134,0.4)', fontSize: 9 }} />
                  <Radar name="Rating" dataKey="value" stroke="#493986" fill="#493986" fillOpacity={0.5} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <button
              data-testid="save-agenda-rating-button"
              onClick={handleSaveRating}
              disabled={saving}
              className="w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl shadow-lg disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Rating"}
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 px-4 pb-8 overflow-y-auto">
        <div className={`text-center mb-6 ${theme.textPrimary}`}>
          <p className="text-3xl font-bold">{today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
        </div>

        <div className="space-y-3">
          {displayActivities.length === 0 ? (
            <div className={`text-center py-8 ${theme.textMuted}`}>
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{isCoach ? 'No upcoming sessions' : 'No activities'}</p>
              <p className="text-sm mt-1">Tap + to add a new activity</p>
            </div>
          ) : displayActivities.map((activity) => {
            const Icon = getActivityIcon(activity.type);
            const activitySession = getSessionForActivity(activity);
            const isRated = activitySession?.rating;
            const daysAgo = activity.isPast ? Math.ceil((today - activity.date) / (1000 * 60 * 60 * 24)) : 0;
            const daysUntil = !activity.isPast ? Math.ceil((activity.date - today) / (1000 * 60 * 60 * 24)) : 0;
            const assignedGoals = assignedGoalsFor(activity);
            const isCustom = activity.id.startsWith('custom_');
            const canEdit = canEditActivity(activity);
            const canDelete = canDeleteActivity(activity);
            const attendanceCount = isCoach ? getAttendanceCount(activity) : 0;
            
            return (
              <div
                key={activity.id}
                data-testid={`activity-${activity.id}`}
                onClick={() => {
                  if (isCoach) {
                    handleCoachActivityClick(activity);
                  } else {
                    activity.isPast ? handleActivityClick(activity) : handleFutureActivityClick(activity);
                  }
                }}
                className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl transition-all cursor-pointer hover:scale-[1.02] ${
                  !isCoach && activity.isPast && !isRated
                    ? isDark ? 'border-orange-400/80 shadow-[0_0_0_1px_rgba(251,146,60,0.25)]' : 'border-orange-400 bg-orange-50/60'
                    : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      activity.isPast 
                        ? isRated 
                          ? 'bg-green-500/20' 
                          : 'bg-orange-500/20'
                        : 'bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20'
                    }`}>
                      {activity.groupPhotoUrl ? <PhotoAvatar photoUrl={activity.groupPhotoUrl} name={activity.groupName} className="h-12 w-12 rounded-xl" /> : isRated ? (
                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                      ) : (
                        <Icon className={`w-6 h-6 ${activity.isPast ? 'text-orange-500' : theme.textPrimary}`} />
                      )}
                    </div>
                    <div>
                      <h3 className={`font-semibold ${theme.textPrimary}`}>{activity.title}</h3>
                      <p className={`text-sm ${theme.textMuted}`}>
                        {formatDate(activity.date)} • {activity.time || new Date(activity.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {activity.categoryLabel && (
                        <span className={`text-xs ${theme.textMuted}`}>{activity.categoryLabel}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-1">
                    <p className={`text-sm font-medium ${theme.textSecondary}`}>{activity.duration} min</p>
                    <p className={`text-xs ${theme.textMuted}`}>{activity.location}</p>
                    {isCoach && activity.groupName && (
                      <p
                        className={`text-xs ${theme.textMuted}`}
                        data-testid={`activity-group-name-${activity.id}`}
                      >
                        Group: {activity.groupName}
                      </p>
                    )}
                    {isCoach && (
                      <div className="flex items-center gap-1 mt-1">
                        <Users className={`w-3 h-3 ${theme.textMuted}`} />
                        <span className={`text-xs ${theme.textMuted}`}>{attendanceCount}/{teamAthletes.length}</span>
                      </div>
                    )}
                    {!isCoach && activity.isPast && !isRated && (
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-700'}`}
                        data-testid={`activity-pending-label-${activity.id}`}
                      >
                        Rate this session
                      </span>
                    )}
                    {(canEdit || canDelete) && (
                      <div className="flex items-center gap-1 mt-1" data-testid={`activity-actions-${activity.id}`}>
                        {canEdit && (
                          <button
                            data-testid={`edit-activity-${activity.id}`}
                            onClick={(e) => { e.stopPropagation(); handleOpenEditActivity(activity); }}
                            className={`p-1 rounded-lg ${theme.button}`}
                          >
                            <Pencil className={`w-4 h-4 ${theme.textMuted}`} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            data-testid={`delete-activity-${activity.id}`}
                            onClick={(e) => { e.stopPropagation(); handleDeleteActivity(activity); }}
                            className={`p-1 rounded-lg ${theme.button}`}
                          >
                            <Trash2 className={`w-4 h-4 ${theme.textMuted}`} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!isCoach && activity.isPast && (
                  <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
                    {isRated ? (
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2 flex-wrap">
                          <span className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'}`}>
                            Fun: {formatRating(activitySession.rating.fun)}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                            Learn: {formatRating(activitySession.rating.learning)}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                            Goal: {formatRating(activitySession.rating.mygoal)}
                          </span>
                        </div>
                        <span className={`text-xs ${theme.textMuted}`}>Tap to update</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium text-orange-500`}>
                          {daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`} - Rate now!
                        </span>
                        <ChevronLeft className="w-5 h-5 text-orange-500 rotate-180" />
                      </div>
                    )}
                  </div>
                )}

                {!isCoach && !activity.isPast && (
                  <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`text-xs ${theme.textMuted}`}>
                          {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                        </span>
                        {assignedGoals.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {assignedGoals.map(g => (
                              <span key={g.id} className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                                {g.title}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={`text-xs ${assignedGoals.length > 0 ? 'text-purple-500' : theme.textMuted}`}>
                          {assignedGoals.length > 0 ? `${assignedGoals.length} goals` : 'Set goals'}
                        </span>
                        <ChevronLeft className={`w-4 h-4 rotate-180 ${assignedGoals.length > 0 ? 'text-purple-500' : theme.textMuted}`} />
                      </div>
                    </div>
                  </div>
                )}

                {isCoach && !activity.isPast && (
                  <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${theme.textMuted}`}>
                        {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                      </span>
                      <span className={`text-xs ${theme.textMuted}`}>
                        Tap to manage
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {hasHiddenAthleteActivities && (
            <button
              type="button"
              onClick={() => setShowAllAthleteActivities((prev) => !prev)}
              className={`w-full rounded-2xl border px-4 py-3 flex items-center justify-center gap-2 transition-colors ${isDark ? 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10' : 'border-[#493986]/10 bg-white/70 text-[#493986] hover:bg-[#493986]/5'}`}
              data-testid="agenda-show-more-button"
            >
              {showAllAthleteActivities ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Minder laten zien
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Klik om meer te zien
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Coach Activity Detail Modal */}
      {showActivityDetail && selectedActivity && isCoach && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={() => { setShowActivityDetail(false); setSelectedActivity(null); }}>
          <div 
            className={`relative w-full max-h-[90vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4 pb-8`}
            style={{ 
              transform: `translateY(${activityDetailSwipe.translateY}px)`,
              transition: activityDetailSwipe.isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            onTouchStart={activityDetailSwipe.onTouchStart}
            onTouchMove={activityDetailSwipe.onTouchMove}
            onTouchEnd={activityDetailSwipe.onTouchEnd}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center">
                {(() => {
                  const Icon = getActivityIcon(selectedActivity.type);
                  return <Icon className={`w-6 h-6 ${theme.textPrimary}`} />;
                })()}
              </div>
              <div>
                <h2 className={`text-xl font-bold ${theme.textPrimary}`}>{selectedActivity.title}</h2>
                <p className={`text-sm ${theme.textMuted}`}>
                  {formatDate(selectedActivity.date)} • {selectedActivity.time} • {selectedActivity.duration} min
                </p>
              </div>
            </div>

            {(canEditActivity(selectedActivity) || canDeleteActivity(selectedActivity)) && (
              <div className="space-y-2 mb-4" data-testid="activity-detail-actions">
                <div className="grid grid-cols-2 gap-2">
                  {canEditActivity(selectedActivity) && (
                    <button
                      data-testid="activity-detail-edit-button"
                      onClick={() => handleOpenEditActivity(selectedActivity)}
                      className={`py-2.5 rounded-xl font-medium ${theme.button} ${theme.textPrimary}`}
                    >
                      Edit Activity
                    </button>
                  )}
                  {canDeleteActivity(selectedActivity) && (
                    <button
                      data-testid="activity-detail-delete-button"
                      onClick={() => handleDeleteActivity(selectedActivity)}
                      className="py-2.5 rounded-xl font-medium bg-red-500 text-white"
                    >
                      Delete Activity
                    </button>
                  )}
                </div>
                {/* Delete all in series button - only show if activity is part of a series */}
                {canDeleteActivity(selectedActivity) && selectedActivity.series_id && getSeriesCount(selectedActivity) > 1 && (
                  <button
                    data-testid="activity-detail-delete-series-button"
                    onClick={() => handleDeleteSeries(selectedActivity)}
                    className={`w-full py-2.5 rounded-xl font-medium border-2 border-red-500 text-red-500 ${isDark ? 'hover:bg-red-500/10' : 'hover:bg-red-50'}`}
                  >
                    Delete All in Series ({getSeriesCount(selectedActivity)} activities)
                  </button>
                )}
              </div>
            )}


            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`font-semibold ${theme.textPrimary}`}>Attendance</h3>
                <span className={`text-sm ${theme.textMuted}`}>
                  {getAttendanceCount(selectedActivity)}/{attendanceSummary[selectedActivity.id]?.total_athletes || getActivityAthletes(selectedActivity).length} attending
                </span>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(attendanceDetails[selectedActivity.id] || getActivityAthletes(selectedActivity).map((athlete) => ({
                  athlete_id: athlete.id,
                  athlete_name: athlete.name,
                  attending: attendance[selectedActivity.id]?.[athlete.id] ?? true,
                }))).map((athlete) => {
                  const isAttending = athlete.attending;
                  return (
                    <div 
                      key={athlete.athlete_id}
                      className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                          <User className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                        </div>
                        <span className={`text-sm ${theme.textPrimary}`}>{athlete.athlete_name}</span>
                      </div>
                      <button
                        onClick={() => toggleAttendance(selectedActivity, athlete.athlete_id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          isAttending 
                            ? 'bg-green-500 text-white' 
                            : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-200 text-gray-600'
                        }`}
                        data-testid={`coach-attendance-toggle-${athlete.id}`}
                      >
                        {isAttending ? '✓ Present' : 'Mark Present'}
                      </button>
                    </div>
                  );
                })}
                {(attendanceDetails[selectedActivity.id] || []).length === 0 && getActivityAthletes(selectedActivity).length === 0 && (
                  <p className={`text-center py-4 ${theme.textMuted}`}>No athletes in your team</p>
                )}
              </div>
            </div>

            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
              <h3 className={`font-semibold mb-3 ${theme.textPrimary}`}>Goals for this Session</h3>
              <p className={`text-xs mb-3 ${theme.textMuted}`}>Each athlete can set 1 goal per activity</p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {activityGoalsFromDb.length > 0 ? (
                  activityGoalsFromDb.map((goal) => (
                    <div key={goal.id} className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                            <User className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                          </div>
                          <div>
                            <span className={`text-sm font-medium ${theme.textPrimary}`}>{goal.athlete_name}</span>
                            <span className={`block text-xs ${
                              goal.category === 'technical' ? 'text-blue-500' :
                              goal.category === 'physical' ? 'text-green-500' :
                              goal.category === 'tactical' ? 'text-purple-500' :
                              'text-orange-500'
                            }`}>{goal.category}</span>
                            <span className={`block text-[11px] ${goal.attending ? 'text-green-500' : 'text-red-500'}`}>
                              {goal.attending ? 'Present' : 'Absent'}
                            </span>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          goal.category === 'technical' ? (isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600') :
                          goal.category === 'physical' ? (isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600') :
                          goal.category === 'tactical' ? (isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600') :
                          (isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600')
                        }`}>
                          {goal.title}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className={`text-center py-4 ${theme.textMuted}`}>No goals set for this activity yet</p>
                )}
              </div>
              
              {activityGoalsFromDb.length > 0 && (
                <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
                  <p className={`text-xs ${theme.textMuted}`}>
                    {activityGoalsFromDb.length} of {getActivityAthletes(selectedActivity).length} athletes have set a goal
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={() => { setShowActivityDetail(false); setSelectedActivity(null); }}
              className="w-full mt-4 py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ====== Edit Scope Choice Modal (recurring series) ====== */}
      {showEditScopeModal && pendingEditActivity && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="edit-scope-modal">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => { setShowEditScopeModal(false); setPendingEditActivity(null); }}
          />
          <div className={`relative w-full max-w-sm rounded-3xl p-6 ${theme.card} border backdrop-blur-xl`}>
            <h3 className={`text-lg font-bold mb-2 ${theme.textPrimary}`}>Edit recurring activity</h3>
            <p className={`text-sm mb-5 ${theme.textMuted}`}>
              This activity is part of a series of {getSeriesCount(pendingEditActivity)} occurrences.
              How would you like to apply your changes?
            </p>
            <div className="space-y-2">
              <button
                data-testid="edit-scope-single-btn"
                onClick={() => {
                  const a = pendingEditActivity;
                  setShowEditScopeModal(false);
                  setPendingEditActivity(null);
                  openEditFormForActivity(a, 'single');
                }}
                className={`w-full text-left p-3 rounded-2xl border ${theme.border} ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-[#493986]/5 hover:bg-[#493986]/10'} transition-colors`}
              >
                <div className={`font-medium ${theme.textPrimary}`}>Only this occurrence</div>
                <div className={`text-xs mt-0.5 ${theme.textMuted}`}>Other activities in this series stay unchanged.</div>
              </button>
              <button
                data-testid="edit-scope-series-btn"
                onClick={() => {
                  const a = pendingEditActivity;
                  setShowEditScopeModal(false);
                  setPendingEditActivity(null);
                  openEditFormForActivity(a, 'series');
                }}
                className="w-full text-left p-3 rounded-2xl text-white"
                style={{ background: 'linear-gradient(135deg, #493986 0%, #6b5b9a 100%)' }}
              >
                <div className="font-medium">All {getSeriesCount(pendingEditActivity)} in this series</div>
                <div className="text-xs mt-0.5 text-white/80">Apply your changes to every occurrence. Each date stays the same — only time-of-day, title, group, type, location and duration change.</div>
              </button>
            </div>
            <button
              data-testid="edit-scope-cancel-btn"
              onClick={() => { setShowEditScopeModal(false); setPendingEditActivity(null); }}
              className={`w-full mt-3 py-2.5 rounded-2xl text-sm font-medium ${theme.textMuted}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendaPage;
